import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Chip } from '../ui/Chip';
import { Field } from '../ui/Field';
import { SegmentedChoice } from '../ui/SegmentedChoice';
import { Sheet } from '../ui/Sheet';
import { LOAD_ICON_MAP, LOAD_ICON_ORDER } from '../../../lib/rcu/catalog/icon';
import type { LoadIconKey } from '../../../lib/rcu/catalog/icon';
import { LOAD_TYPE_KEYS, LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import type { LoadTypeKey } from '../../../lib/rcu/catalog/loadType';
import type { ChannelRef } from '../../../lib/rcu/catalog/channel';
import type { Load, RcuConfig } from '../../../lib/rcu/model';
import { findRoom } from '../../../lib/rcu/query/lookup';
import { targetFromRef } from '../../../lib/rcu/query/targets';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface LoadSheetProps {
  isOpen: boolean;
  /** null creates a load, otherwise the one being edited. */
  load: Load | null;
  /** Pre-selects an output when creating from a free channel. */
  channel: ChannelRef | null;
  onClose: () => void;
}

const UNASSIGNED = 'unassigned';

const typeChoices = (config: RcuConfig, load: Load | null) => {
  const currentChannel = load?.channel ?? null;
  const target = currentChannel === null ? null : targetFromRef(config, currentChannel);

  return LOAD_TYPE_KEYS.map((type) => {
    const spec = LOAD_TYPE_SPECS[type];
    const blocked = target !== null && !target.acceptedLoadTypes.includes(type);
    return {
      value: type,
      label: spec.label,
      description: blocked
        ? `Not possible: ${target.shortLabel} drives ${target.outputHint} only`
        : `${spec.outputHint} · wired to ${spec.label.toLowerCase()}`,
      disabled: blocked,
    };
  });
};

/** Add or edit a load: what it is called, how it is driven, where it lives. */
export const LoadSheet: React.FC<LoadSheetProps> = ({ isOpen, load, channel, onClose }) => {
  const config = useRcuConfigStore((state) => state.config);
  const addLoad = useRcuConfigStore((state) => state.addLoad);
  const updateLoad = useRcuConfigStore((state) => state.updateLoad);
  const report = useCommandFeedback();

  const [name, setName] = React.useState(load?.name ?? '');
  const [type, setType] = React.useState<LoadTypeKey>(load?.type ?? LOAD_TYPE_KEYS[0]);
  const [iconKey, setIcon] = React.useState<LoadIconKey>(
    load?.iconKey ?? LOAD_TYPE_SPECS[load?.type ?? LOAD_TYPE_KEYS[0]].defaultIcon,
  );
  const [wattage, setWattage] = React.useState(load?.wattage === null || load?.wattage === undefined ? '' : String(load.wattage));
  const [roomChoice, setRoomChoice] = React.useState<string>(load?.roomId ?? UNASSIGNED);

  const roomChoices = [
    { value: UNASSIGNED, label: 'Unassigned', description: 'Shown under the Unassigned tab' },
    ...config.rooms.map((room) => ({
      value: room.id,
      label: room.name,
      description: room.note === '' ? undefined : room.note,
    })),
  ];

  const chooseType = (next: LoadTypeKey): void => {
    setType(next);
    // The default glyph belongs to the type, so follow it unless it was customised.
    if (load === null || load.iconKey === LOAD_TYPE_SPECS[load.type].defaultIcon) {
      setIcon(LOAD_TYPE_SPECS[next].defaultIcon);
    }
  };

  const handleSave = (): void => {
    const parsedWattage = wattage.trim() === '' ? null : Number(wattage);
    const watts = parsedWattage !== null && Number.isFinite(parsedWattage) ? parsedWattage : null;
    const roomId =
      roomChoice === UNASSIGNED
        ? null
        : config.rooms.find((room) => room.id === roomChoice)?.id ?? null;

    if (load === null) {
      if (report(addLoad({ name, type, roomId, channel, wattage: watts, iconKey }), 'Load added.')) {
        onClose();
      }
      return;
    }

    if (report(updateLoad(load.id, { name, type, iconKey, wattage: watts, roomId }), 'Load saved.')) {
      onClose();
    }
  };

  const target = channel === null ? null : targetFromRef(config, channel);
  const wiredRoom = load?.roomId == null ? null : findRoom(config, load.roomId)?.name ?? null;

  return (
    <Sheet
      isOpen={isOpen}
      title={load === null ? 'Add load' : 'Edit load'}
      subtitle={target === null ? undefined : `Wiring to ${target.label}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={name.trim() === ''}>
            {load === null ? 'Add load' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {load !== null && (
          <div className="flex flex-wrap items-center gap-2">
            <Chip label={LOAD_TYPE_SPECS[load.type].label} />
            <Chip label={wiredRoom ?? 'Not in a room'} />
          </div>
        )}

        <Field
          label="Load name"
          value={name}
          onChange={setName}
          placeholder="Bedside reading light"
          maxLength={40}
          autoFocus
        />

        <SegmentedChoice
          label="Load type"
          options={typeChoices(config, load)}
          value={type}
          onChange={chooseType}
          columns={1}
          hint={
            load?.channel == null
              ? 'The type decides how a guest controls this load and which outputs can drive it.'
              : 'Types its current output cannot drive are unavailable.'
          }
        />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-[#333333]">Icon</span>
          <div className="grid grid-cols-6 gap-2">
            {LOAD_ICON_ORDER.map((key) => {
              const Icon = LOAD_ICON_MAP[key];
              const isSelected = key === iconKey;
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={key}
                  onClick={() => setIcon(key)}
                  className={`flex h-11 items-center justify-center rounded-xl border transition-colors ${
                    isSelected
                      ? 'border-[#D4A054] bg-[#FDF6E9] text-[#D4A054]'
                      : 'border-[#E0E0E0] bg-white text-[#666666] hover:border-[#D4A054]/60'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </button>
              );
            })}
          </div>
        </div>

        <Field
          label="Wattage"
          value={wattage}
          onChange={setWattage}
          placeholder="Optional"
          inputMode="numeric"
          hint="Optional, in watts. Helps when checking a circuit's budget."
        />

        <SegmentedChoice
          label="Room"
          options={roomChoices}
          value={roomChoice}
          onChange={setRoomChoice}
          columns={1}
          hint="Guest controls group loads by room. Unassigned loads stay controllable."
        />
      </div>
    </Sheet>
  );
};

export default LoadSheet;
